import React, { Component, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
}

/**
 * Error Boundary component to catch and handle React component errors gracefully.
 * Prevents the entire app from crashing when a component throws an error.
 * 
 * @example
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log error to console in development
        console.error('Error boundary caught an error:', error, errorInfo);

        // TODO: Send to error tracking service (e.g., Sentry) in production
        if (import.meta.env.PROD) {
            // Example: Sentry.captureException(error, { extra: errorInfo });
        }
    }

    render() {
        if (this.state.hasError) {
            // Render custom fallback UI or use provided fallback
            return this.props.fallback || (
                <div className="min-h-screen flex items-center justify-center bg-background">
                    <div className="text-center max-w-md mx-auto px-4">
                        <div className="mb-6">
                            <svg
                                className="w-16 h-16 mx-auto text-destructive"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                            </svg>
                        </div>

                        <h1 className="text-2xl font-bold text-foreground mb-3">
                            Oops! Something went wrong
                        </h1>

                        <p className="text-xl text-muted-foreground mb-6">
                            We're sorry for the inconvenience. The page encountered an error.
                        </p>

                        <button
                            onClick={() => window.location.reload()}
                            className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
                        >
                            Reload page
                        </button>

                        {import.meta.env.DEV && this.state.error && (
                            <details className="mt-6 text-left bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                                <summary className="cursor-pointer font-medium text-destructive mb-2">
                                    Error details (dev only)
                                </summary>
                                <pre className="text-sm text-foreground/80 overflow-auto">
                                    {this.state.error.toString()}
                                    {this.state.error.stack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
